/**
 * status-line.tsx — a line said to a screen reader and shown to no one: a
 * polite live region (role="status"), visually hidden, that says what it
 * holds as that changes — a verdict, a phase's title, what stands in the way.
 *
 * A region that arrives with its words already in it is not reliably
 * announced: a screen reader says a change to what a region holds, and a
 * region new to the page has had none. Nor is one whose words come in the
 * same frame it does: the browser builds its accessibility tree once a frame,
 * so a screen reader would still meet the region holding them. So the line
 * mounts empty and holds back its first words until it has been on the page
 * two frames and a tenth of a second (useSettled, ui/use-settled.ts, which
 * the portal's StatusLine waits on too) —
 * wherever it is mounted: in a panel opened on demand, as a pair's share is,
 * or in a phase as it begins. From then on it says each change at once. One
 * moved from the page to above it is a region new to the page there, and
 * waits again. design:check holds every hidden status line to this one
 * (status-line-by-hand).
 *
 * Where it stands decides whether it is heard while a dialog is open. In the
 * page, the default, it is inert with the page under a dialog and goes quiet
 * with it: what belongs to the page — the route builder's reason, the
 * missions' progress. `overDialogs` stands it above the page, beside the
 * dialogs, so it is heard over any of them: what concerns the whole game or a
 * dialog of its own — a slow connection, a quiz's verdict and its time running
 * out, the dialogue's line, the team activity's phase. Modal sets no
 * aria-modal, so nothing prunes it there (ui/modal.tsx).
 *
 *   <StatusLine>{inTheWay}</StatusLine>
 *   <StatusLine overDialogs>{verdict}</StatusLine>
 */

import { abovePage } from './page-layers';
import { useSettled } from './use-settled';
import utilities from '../../styles/utilities.module.css';

interface StatusLineProps {
  /** What it says now, in words; nothing while there is nothing to say. */
  children?: string;
  /** Heard while a dialog is open: it stands above the page, beside the dialogs, not in the inert page. */
  overDialogs?: boolean;
}

export function StatusLine({ children = '', overDialogs = false }: StatusLineProps) {
  const line = <Line words={children} />;
  // No rung: it is drawn nowhere, and holds nothing that could hang from it.
  return overDialogs ? abovePage(line, null) : line;
}

/** The region itself: empty until it has been on the page long enough to be heard (useSettled), then its words. */
function Line({ words }: { words: string }) {
  const settled = useSettled();
  return (
    <p role="status" className={utilities.visuallyHidden}>
      {settled ? words : ''}
    </p>
  );
}
